import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import {
  getCheckoutStatus,
  getBillingPlans,
  createPortalSession,
} from "../lib/api/billing";
import { getUsageSummary } from "../lib/api/usage";
import {
  Loader2,
  CheckCircle,
  ArrowLeft,
  AlertCircle,
  Settings,
  Sparkles,
  ShieldCheck,
  CreditCard,
  MapPin,
} from "lucide-react";
import intelligentiaLogo from "../images/intelligentia.jpg";

type Tier = {
  name: string;
  displayName: string;
  eyebrow: string;
  priceLabel: string;
  pricePeriod: string;
  pricePeriodLong?: string;
  monthlyEquiv?: string;
  strike?: string;
  savingsBadge?: string;
  ribbon?: string;
  quotaPrimary: string;
  quotaSecondary?: string;
  features: string[];
  previewFeatures?: string[];
  footnote: string;
  featured: boolean;
  preview?: boolean;
};

const TIERS: Tier[] = [
  {
    name: "mensuel",
    displayName: "Mensuel",
    eyebrow: "Pour commencer",
    priceLabel: "135 $",
    pricePeriod: "CAD / mois",
    pricePeriodLong: "Facturation mensuelle",
    quotaPrimary: "20 questions par mois",
    features: [
      "Réponses sourcées tirées de la documentation officielle",
      "Recherche dans l'ensemble de la base documentaire",
      "Historique de vos conversations conservé",
      "Annulation en un clic",
    ],
    footnote: "Idéal pour essayer la plateforme un mois à la fois.",
    featured: false,
  },
  {
    name: "6mois",
    displayName: "6 mois",
    eyebrow: "Le plus populaire",
    priceLabel: "700 $",
    pricePeriod: "CAD / 6 mois",
    savingsBadge: "Économisez 14 %",
    ribbon: "Meilleure valeur",
    quotaPrimary: "140 questions sur 6 mois",
    quotaSecondary: "≈ 23 questions / mois",
    features: [
      "Tout ce qui est inclus dans Mensuel",
      "40 % plus de questions par mois vs. Mensuel",
      "Coût par question le plus bas",
      "Renouvellement semestriel — moins de gestion",
      "Support prioritaire",
    ],
    footnote: "Le bon équilibre entre engagement court et économies réelles.",
    featured: true,
  },
  {
    name: "annuel",
    displayName: "Annuel",
    eyebrow: "Engagement long terme",
    priceLabel: "1 400 $",
    pricePeriod: "CAD / an",
    savingsBadge: "Prix verrouillé 12 mois",
    quotaPrimary: "240 questions sur 12 mois",
    quotaSecondary: "≈ 20 questions / mois",
    features: [
      "Tout ce qui est inclus dans Mensuel",
      "Prix verrouillé pendant 12 mois",
      "Une seule facture par année",
      "Support prioritaire",
    ],
    previewFeatures: [
      "Agent de description de fonctions",
      "Agent de rémunération",
    ],
    footnote:
      "Pour les organisations qui préfèrent prévoir leurs dépenses sur l'année.",
    featured: false,
    preview: true,
  },
];

const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  {
    q: "Que se passe-t-il si je dépasse mon quota de questions ?",
    a: "Une fois votre quota atteint, l'assistant vous invite à passer au forfait supérieur ou à attendre le renouvellement de la période. Aucun frais surprise.",
  },
  {
    q: "Puis-je changer de forfait en cours de route ?",
    a: "Oui. Vous pouvez passer d'un forfait à l'autre à tout moment depuis votre espace de facturation. Le solde restant est appliqué au prorata.",
  },
  {
    q: "Les questions inutilisées se reportent-elles ?",
    a: "Non. Le quota est réinitialisé à chaque renouvellement de période (mensuel, semestriel ou annuel selon votre forfait).",
  },
  {
    q: "Quels modes de paiement acceptez-vous ?",
    a: "Visa, Mastercard et American Express via Stripe. Les paiements sont sécurisés et conformes aux standards PCI-DSS.",
  },
  {
    q: "Y a-t-il un essai gratuit ?",
    a: "Contactez-nous pour discuter d'un accès d'évaluation adapté à votre organisation.",
  },
];

export function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState<string | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [isStripeManaged, setIsStripeManaged] = useState(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getBillingPlans().catch(() => null),
      getUsageSummary().catch(() => null),
    ]).then(([_plans, usage]) => {
      if (cancelled) return;
      if (usage?.plan_name) setActivePlan(usage.plan_name);
      if (usage) setIsStripeManaged(usage.is_stripe_managed);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const pollCheckoutStatus = useCallback(
    (sid: string, signal: { cancelled: boolean }) => {
      setPolling(true);
      let attempts = 0;
      const maxAttempts = 15;

      const poll = async () => {
        if (signal.cancelled) return;
        if (attempts >= maxAttempts) {
          setPolling(false);
          setPollingTimedOut(true);
          return;
        }
        try {
          const result = await getCheckoutStatus(sid);
          if (signal.cancelled) return;
          if (result.status === "complete") {
            setPolling(false);
            navigate("/chat", { replace: true });
            return;
          }
        } catch {
          // Continue polling
        }
        attempts++;
        if (!signal.cancelled) {
          setTimeout(poll, 2000);
        }
      };

      poll();
    },
    [navigate],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    if (sessionId) {
      pollCheckoutStatus(sessionId, signal);
    }
    return () => {
      signal.cancelled = true;
    };
  }, [sessionId, pollCheckoutStatus]);

  const handleSubscribe = (tierName: string) => {
    setLoading(tierName);
    setError(null);
    // Backend wiring for the new tiers is pending — surface a polite notice
    // rather than firing a checkout against plans that don't yet exist.
    setNotice(
      "Activation en cours de configuration. Contactez-nous pour souscrire dès maintenant.",
    );
    setLoading(null);
  };

  const handleManageSubscription = async () => {
    setLoading("portal");
    setError(null);
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          "Une erreur est survenue. Veuillez réessayer.",
      );
      setLoading(null);
    }
  };

  if (pollingTimedOut && sessionId) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-brand-teal mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brand-text mb-2">
            Votre paiement est en cours de traitement
          </h2>
          <p className="text-brand-muted mb-6">
            L'activation peut prendre quelques instants. Votre abonnement sera
            actif dès la confirmation du paiement.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setPollingTimedOut(false);
                const signal = { cancelled: false };
                pollCheckoutStatus(sessionId, signal);
              }}
              className="w-full py-3 px-4 bg-brand-bg border border-brand-border hover:border-brand-primary text-brand-text font-medium rounded-pill transition-colors flex items-center justify-center gap-2"
            >
              <Loader2 className="w-4 h-4" />
              Vérifier à nouveau
            </button>
            <button
              onClick={() => navigate("/chat", { replace: true })}
              className="text-sm text-brand-muted hover:text-brand-text transition-colors"
            >
              Retourner au chat
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (polling) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand-muted animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brand-text mb-2">
            Configuration de votre compte...
          </h2>
          <p className="text-brand-muted">
            Veuillez patienter pendant que nous activons votre forfait.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface relative overflow-hidden">
      {/* Aura background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-0"
      >
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-30 blur-3xl bg-gradient-to-br from-[#744796] to-[#48b2cd]" />
      </div>

      {/* Nav bar */}
      <nav className="relative z-10 flex items-center justify-between px-4 sm:px-8 py-4 bg-brand-bg/80 backdrop-blur-sm border-b border-brand-border">
        <div className="flex items-center gap-2.5">
          <img
            src={intelligentiaLogo}
            alt="Intelligentia Humana"
            className="h-6 w-6 object-contain rounded-md"
          />
          <span className="text-[15px] font-semibold text-brand-text tracking-wide">
            Intelligentia Humana
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/chat")}
            className="flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Retour
          </button>
          <UserButton
            appearance={{
              elements: {
                avatarBox: "h-8 w-8",
              },
            }}
          />
        </div>
      </nav>

      {/* Hero */}
      <div className="relative z-10 max-w-6xl w-full mx-auto px-4 pt-16 pb-10">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-brand-muted mb-3">
            Tarifs
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-brand-text mb-4 tracking-tight">
            {activePlan
              ? "Votre abonnement"
              : "Choisissez le rythme qui vous convient"}
          </h1>
          <p className="text-base sm:text-lg text-brand-muted max-w-2xl mx-auto">
            {activePlan
              ? `Vous êtes abonné au forfait ${activePlan}. Gérez votre abonnement ci-dessous.`
              : "Posez vos questions et obtenez des réponses sourcées tirées de la documentation officielle. Tous les forfaits incluent l'accès complet à l'assistant Intelligentia Humana et le support prioritaire. Annulation possible en tout temps."}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-brand-error/10 border border-brand-error/25 rounded-[10px] text-brand-error text-sm text-center flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {notice && (
          <div className="mb-6 p-4 bg-brand-teal/10 border border-brand-teal/25 rounded-[10px] text-brand-teal/90 text-sm text-center flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            {notice}
          </div>
        )}

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-5 items-stretch">
          {TIERS.map((tier) => {
            const isCurrentPlan =
              activePlan?.toLowerCase() === tier.displayName.toLowerCase();
            const isLoading = loading === tier.name;

            const card = (
              <>
                {tier.preview && (
                  <span
                    className={`self-start inline-flex items-center gap-1.5 mb-3 text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-pill border ${
                      tier.featured
                        ? "bg-brand-surface/15 text-brand-surface border-brand-surface/25"
                        : "bg-brand-teal/10 text-brand-teal border-brand-teal/25"
                    }`}
                  >
                    <Sparkles className="w-3 h-3" aria-hidden="true" />
                    Aperçu · Bientôt disponible
                  </span>
                )}
                {tier.eyebrow && (
                  <p
                    className={`text-xs font-semibold tracking-wider uppercase mb-2 ${
                      tier.featured ? "text-brand-surface/80" : "text-brand-muted"
                    }`}
                  >
                    {tier.eyebrow}
                  </p>
                )}
                <h3
                  className={`text-2xl font-semibold mb-1 ${
                    tier.featured ? "text-brand-surface" : "text-brand-text"
                  }`}
                >
                  {tier.displayName}
                </h3>

                <div className="mt-5 mb-5">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`text-5xl font-thin ${
                        tier.featured
                          ? "text-brand-surface"
                          : "text-brand-text"
                      }`}
                    >
                      {tier.priceLabel}
                    </span>
                    <span
                      className={`text-sm ${
                        tier.featured
                          ? "text-brand-surface/70"
                          : "text-brand-muted"
                      }`}
                    >
                      {tier.pricePeriod}
                    </span>
                  </div>
                  {tier.monthlyEquiv && (
                    <p
                      className={`mt-2 text-sm ${
                        tier.featured
                          ? "text-brand-surface/80"
                          : "text-brand-muted"
                      }`}
                    >
                      {tier.monthlyEquiv}
                      {tier.strike && (
                        <span
                          className={`ml-2 line-through ${
                            tier.featured
                              ? "text-brand-surface/50"
                              : "text-brand-muted/60"
                          }`}
                        >
                          {tier.strike}
                        </span>
                      )}
                    </p>
                  )}
                  {tier.pricePeriodLong && !tier.monthlyEquiv && (
                    <p
                      className={`mt-2 text-sm ${
                        tier.featured
                          ? "text-brand-surface/70"
                          : "text-brand-muted"
                      }`}
                    >
                      {tier.pricePeriodLong}
                    </p>
                  )}
                  {tier.savingsBadge && (
                    <span
                      className={`inline-block mt-3 text-xs font-semibold px-2.5 py-1 rounded-pill ${
                        tier.featured
                          ? "bg-brand-surface/15 text-brand-surface"
                          : "bg-brand-teal/15 text-brand-teal"
                      }`}
                    >
                      {tier.savingsBadge}
                    </span>
                  )}
                </div>

                <div
                  className={`mb-5 pb-5 border-b ${
                    tier.featured
                      ? "border-brand-surface/15"
                      : "border-brand-border"
                  }`}
                >
                  <p
                    className={`text-sm font-medium ${
                      tier.featured ? "text-brand-surface" : "text-brand-text"
                    }`}
                  >
                    {tier.quotaPrimary}
                  </p>
                  {tier.quotaSecondary && (
                    <p
                      className={`text-xs mt-1 ${
                        tier.featured
                          ? "text-brand-surface/60"
                          : "text-brand-muted"
                      }`}
                    >
                      {tier.quotaSecondary}
                    </p>
                  )}
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {tier.features.map((feature) => (
                    <li
                      key={feature}
                      className={`flex items-start gap-2 text-sm ${
                        tier.featured
                          ? "text-brand-surface/85"
                          : "text-brand-muted"
                      }`}
                    >
                      <CheckCircle
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          tier.featured
                            ? "text-brand-surface"
                            : "text-brand-teal"
                        }`}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {tier.previewFeatures?.map((feature) => (
                    <li
                      key={feature}
                      className={`flex items-start gap-2 text-sm ${
                        tier.featured
                          ? "text-brand-surface/85"
                          : "text-brand-muted"
                      }`}
                    >
                      <Sparkles
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          tier.featured
                            ? "text-brand-surface"
                            : "text-brand-teal"
                        }`}
                        aria-hidden="true"
                      />
                      <span>
                        {feature}
                        <span
                          className={`ml-2 inline-block align-middle text-[9px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded-pill ${
                            tier.featured
                              ? "bg-brand-surface/15 text-brand-surface"
                              : "bg-brand-teal/15 text-brand-teal"
                          }`}
                        >
                          À venir
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <div
                    className={`w-full py-3 px-4 font-medium rounded-pill text-center text-sm ${
                      tier.featured
                        ? "bg-brand-surface/15 text-brand-surface"
                        : "bg-brand-border/30 border border-brand-border text-brand-muted"
                    }`}
                  >
                    Forfait actuel
                  </div>
                ) : tier.preview ? (
                  <div
                    className={`w-full py-3 px-4 font-medium rounded-pill text-center text-sm flex items-center justify-center gap-2 cursor-not-allowed ${
                      tier.featured
                        ? "bg-brand-surface/10 border border-dashed border-brand-surface/30 text-brand-surface/80"
                        : "bg-brand-border/15 border border-dashed border-brand-border text-brand-muted"
                    }`}
                  >
                    <Sparkles className="w-4 h-4" aria-hidden="true" />
                    Bientôt disponible
                  </div>
                ) : (
                  <button
                    onClick={() => handleSubscribe(tier.name)}
                    disabled={loading !== null}
                    className={
                      tier.featured
                        ? "w-full py-3 px-4 bg-brand-bg text-brand-primary font-semibold rounded-pill transition-all hover:bg-brand-bg/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        : "w-full py-3 px-4 bg-brand-bg border border-brand-border hover:border-brand-primary text-brand-text font-medium rounded-pill transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    }
                  >
                    {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isLoading ? "Redirection..." : "S'abonner"}
                  </button>
                )}

                <p
                  className={`mt-4 text-xs leading-relaxed ${
                    tier.featured
                      ? "text-brand-surface/60"
                      : "text-brand-muted/80"
                  }`}
                >
                  {tier.footnote}
                </p>
              </>
            );

            if (tier.featured) {
              return (
                <div
                  key={tier.name}
                  className="relative md:-my-3 rounded-card p-[2px] bg-gradient-to-br from-[#744796] to-[#48b2cd] shadow-[0_20px_60px_-20px_rgba(116,71,150,0.55)]"
                >
                  {tier.ribbon && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 bg-brand-teal text-brand-surface text-xs font-semibold px-3 py-1 rounded-pill shadow-md">
                      <Sparkles className="w-3 h-3" aria-hidden="true" />
                      {tier.ribbon}
                    </span>
                  )}
                  <div className="bg-brand-primary rounded-[calc(theme(borderRadius.card)-2px)] p-7 flex flex-col h-full">
                    {card}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={tier.name}
                className={
                  tier.preview
                    ? "relative bg-brand-bg/70 border border-dashed border-brand-border/80 rounded-card p-7 flex flex-col shadow-card"
                    : "relative bg-brand-bg border border-brand-border rounded-card p-7 hover:border-brand-primary transition-colors flex flex-col shadow-card"
                }
              >
                {card}
              </div>
            );
          })}
        </div>

        {/* Trust strip */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-brand-muted">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-brand-teal" aria-hidden="true" />
            Paiement sécurisé · PCI-DSS
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CreditCard className="w-4 h-4 text-brand-teal" aria-hidden="true" />
            Visa · Mastercard · Amex via Stripe
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-brand-teal" aria-hidden="true" />
            Données hébergées au Canada
          </span>
        </div>

        {/* Manage subscription button for active users */}
        {activePlan && isStripeManaged && (
          <div className="mt-8 text-center">
            <button
              onClick={handleManageSubscription}
              disabled={loading !== null}
              className="inline-flex items-center gap-2 py-2.5 px-5 bg-brand-bg border border-brand-border hover:border-brand-primary text-brand-muted hover:text-brand-text text-sm font-medium rounded-pill transition-colors disabled:opacity-50"
            >
              {loading === "portal" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Settings className="w-4 h-4" />
              )}
              {loading === "portal" ? "Redirection..." : "Gérer la facturation"}
            </button>
          </div>
        )}
      </div>

      {/* FAQ */}
      <div className="relative z-10 max-w-3xl w-full mx-auto px-4 pb-20">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-text mb-2">
            Questions fréquentes
          </h2>
          <p className="text-sm text-brand-muted">
            Tout ce qu'il faut savoir avant de vous abonner.
          </p>
        </div>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item) => (
            <details
              key={item.q}
              className="group bg-brand-bg border border-brand-border rounded-card open:border-brand-primary/40 transition-colors"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between px-5 py-4 text-sm font-medium text-brand-text">
                <span>{item.q}</span>
                <span
                  aria-hidden="true"
                  className="ml-4 text-brand-muted transition-transform group-open:rotate-45 text-xl leading-none"
                >
                  +
                </span>
              </summary>
              <div className="px-5 pb-5 text-sm text-brand-muted leading-relaxed">
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Final CTA banner */}
      <div className="relative z-10 max-w-6xl w-full mx-auto px-4 pb-20">
        <div className="relative overflow-hidden rounded-card bg-gradient-to-br from-[#744796] to-[#48b2cd] px-8 py-12 sm:py-14 text-center shadow-card">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.6),transparent_60%)]"
          />
          <h2 className="relative text-2xl sm:text-3xl font-bold text-brand-surface mb-3">
            Prêt à commencer ?
          </h2>
          <p className="relative text-sm sm:text-base text-brand-surface/80 mb-6 max-w-xl mx-auto">
            Choisissez le forfait qui correspond à votre rythme de
            consultation. Annulation possible en tout temps.
          </p>
          <div className="relative flex flex-wrap items-center justify-center gap-3">
            {TIERS.map((tier) =>
              tier.preview ? (
                <span
                  key={tier.name}
                  className="inline-flex items-center gap-1.5 py-2.5 px-5 bg-brand-surface/5 border border-dashed border-brand-surface/30 text-brand-surface/70 font-medium rounded-pill text-sm cursor-not-allowed"
                >
                  <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
                  {tier.displayName} · à venir
                </span>
              ) : (
                <button
                  key={tier.name}
                  onClick={() => handleSubscribe(tier.name)}
                  disabled={loading !== null}
                  className={
                    tier.featured
                      ? "py-2.5 px-5 bg-brand-surface text-brand-primary font-semibold rounded-pill text-sm hover:bg-brand-surface/90 transition-colors disabled:opacity-50"
                      : "py-2.5 px-5 bg-brand-surface/10 border border-brand-surface/30 text-brand-surface font-medium rounded-pill text-sm hover:bg-brand-surface/20 transition-colors disabled:opacity-50"
                  }
                >
                  {tier.displayName}
                </button>
              ),
            )}
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-brand-muted">
          Tous les prix sont en dollars canadiens (CAD). Taxes applicables en
          sus.
        </p>
      </div>
    </div>
  );
}
